import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, ExternalLink, Loader2, ShieldCheck, TriangleAlert } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { billingService, type SubscriptionSnapshot } from '@/services/billingService';
import { planService, formatLimit, type PublicPlan } from '@/services/planService';

/**
 * Subscription management: what you're on, what else is available, and how to change it.
 *
 * <p>Everything involving a card happens on Stripe's own pages. This screen only ever hands the
 * browser a URL to go to — no card fields, no payment state machine, and nothing here that could
 * mislead someone into thinking a plan is active before Stripe says so.</p>
 */

/** Stripe's status strings, phrased for someone who isn't reading the Stripe docs. */
function describeStatus(s: SubscriptionSnapshot): { text: string; tone: 'ok' | 'warn' | 'muted' } {
  if (!s.status) return { text: 'No subscription', tone: 'muted' };
  switch (s.status) {
    case 'active':
      return s.cancelAtPeriodEnd
        ? { text: 'Active — ends at the current period', tone: 'warn' }
        : { text: 'Active', tone: 'ok' };
    case 'trialing':
      return { text: 'Trial', tone: 'ok' };
    case 'past_due':
      return { text: "Payment failed — we're retrying your card", tone: 'warn' };
    case 'unpaid':
      return { text: 'Unpaid — access ends at the period end', tone: 'warn' };
    case 'canceled':
      return { text: 'Cancelled', tone: 'muted' };
    default:
      return { text: s.status, tone: 'muted' };
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export const BillingSettingsPage: React.FC = () => {
  const [sub, setSub] = useState<SubscriptionSnapshot | null>(null);
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([billingService.subscription(), planService.listPublic()]);
      setSub(s);
      setPlans(p);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your subscription.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const go = async (label: string, fn: () => Promise<string>) => {
    setBusy(label);
    setError(null);
    try {
      // A full navigation, not a new tab: Stripe Checkout is the rest of this flow, and a
      // popup here would be swallowed by blockers on the one click that matters.
      window.location.href = await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your subscription…
      </div>
    );
  }

  if (sub && !sub.billingEnabled) {
    return (
      <Card className="m-4 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <div className="text-sm font-semibold">Billing isn't set up on this deployment</div>
            <p className="text-xs text-muted-foreground">
              You're on <span className="font-medium text-foreground">{sub.planName}</span>. Plans are
              assigned by an administrator here — there's nothing to pay.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const status = sub ? describeStatus(sub) : null;
  const currentRank = sub?.planRank ?? 0;

  return (
    <div className="space-y-4 p-4">
      {error && (
        <Card className="border-destructive/40 p-3">
          <div className="flex items-start gap-2 text-sm">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Current plan */}
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Current plan</div>
            <div className="text-lg font-semibold">{sub?.planName}</div>
            {status && (
              <div
                className={
                  status.tone === 'ok' ? 'text-xs text-emerald-500'
                  : status.tone === 'warn' ? 'text-xs text-amber-500'
                  : 'text-xs text-muted-foreground'
                }
              >
                {status.text}
              </div>
            )}
            {sub?.currentPeriodEnd && (
              <div className="text-xs text-muted-foreground">
                {sub.cancelAtPeriodEnd ? 'Access ends' : 'Renews'} {formatDate(sub.currentPeriodEnd)}
              </div>
            )}
          </div>

          {sub?.hasCustomer && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy !== null}
              onClick={() => go('portal', billingService.portal)}
            >
              {busy === 'portal'
                ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                : <CreditCard className="mr-2 h-3.5 w-3.5" />}
              Manage billing
              <ExternalLink className="ml-1.5 h-3 w-3 opacity-60" />
            </Button>
          )}
        </div>
      </Card>

      {/* Everything else on offer */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {plans.map(plan => {
          const isCurrent = plan.id === sub?.planId;
          const isDowngrade = plan.rank < currentRank;

          return (
            <Card key={plan.id} className={isCurrent ? 'border-primary p-4' : 'p-4'}>
              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">{plan.name}</span>
                  <span className="text-sm tabular-nums text-muted-foreground">
                    {plan.monthlyPriceUsd > 0 ? `$${plan.monthlyPriceUsd}/mo` : 'Free'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{plan.description}</p>
                <ul className="space-y-0.5 text-xs text-muted-foreground">
                  <li>{formatLimit(plan.highlights.customScanners)} custom scanners</li>
                  <li>{formatLimit(plan.highlights.strategies)} strategies</li>
                  <li>{formatLimit(plan.highlights.backtestMinutesPerMonth)} backtest min/mo</li>
                  {plan.highlights.liveTrading && <li>Live trading</li>}
                  {/* What the assistant may do differs sharply between plans, so it belongs on
                      the card rather than buried in a comparison table. */}
                  <li>
                    {!plan.highlights.aiAssistant ? 'No AI assistant'
                      : plan.highlights.aiLiveTrading ? 'AI: live trading'
                      : plan.highlights.aiTrading ? 'AI: paper trading'
                      : plan.highlights.aiAuthoring ? 'AI: builds & backtests'
                      : 'AI: read-only'}
                  </li>
                </ul>

                <div className="pt-1">
                  {isCurrent ? (
                    <div className="text-xs font-medium text-primary">Your plan</div>
                  ) : !plan.isPurchasable ? (
                    // Either the free tier or a plan with no Stripe price wired up. Saying so
                    // beats an Upgrade button that 400s.
                    <div className="text-xs text-muted-foreground">
                      {plan.monthlyPriceUsd > 0 ? 'Contact us' : 'Included'}
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      className="w-full"
                      variant={isDowngrade ? 'outline' : 'default'}
                      disabled={busy !== null}
                      onClick={() => go(plan.id, () => billingService.checkout(plan.id))}
                    >
                      {busy === plan.id && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                      {isDowngrade ? 'Switch' : 'Upgrade'}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <p className="px-1 text-xs text-muted-foreground">
        Payments are handled by Stripe — card details never reach this application. A new plan
        activates as soon as Stripe confirms the payment, which is usually a few seconds. By
        subscribing you agree to the{' '}
        <a href="#/legal/terms" className="text-primary hover:underline">Terms of Service</a> and{' '}
        <a href="#/legal/risk" className="text-primary hover:underline">Risk Disclaimer</a>.
      </p>
    </div>
  );
};
